import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AlertService } from 'src/core/services/alert.service';
import { BatchService } from 'src/core/services/batch.service';
import { CompanyService } from 'src/core/services/company.service';
import { UtilsService } from 'src/core/services/utils.service';
import { environment } from 'src/environments/environment';
import { PrintLabelPayload } from 'src/interfaces/batch';
import { Company } from 'src/interfaces/company';
import { Individual } from 'src/interfaces/individual';
import { MedicineDosagePackageType } from 'src/interfaces/medicine-dosage-package-type';
import { Order } from 'src/interfaces/order';
import { OrderItem } from 'src/interfaces/order-item';

interface DialogData {
  title: string;
  message: string;
  orderItems: OrderItem[];
  order: Order;
};

@Component({
    selector: 'app-print-labels-dialog',
    templateUrl: './print-labels-dialog.component.html',
    styleUrls: ['./print-labels-dialog.component.scss'],
    standalone: false
})
export class PrintLabelsDialogComponent implements OnInit {

  progressValue: number = 0;

  printLabelsForm = this._formBuilder.group({
    client: [' '],
    prescriber: [' '],
    size: ['']
  })

  selectedClient: Company | null = null;
  selectedPrescriber: Company | null = null;
  largeSize: boolean = false;

  currentItemNumber: number = 0;

  constructor(
    public _dialogRef: MatDialogRef<PrintLabelsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private _batchService: BatchService,
    private _formBuilder: UntypedFormBuilder,
    private _alertService: AlertService,
    private _utilService: UtilsService,
    private _companyService: CompanyService
  ) { }

  ngOnInit(): void {


    let clientName = "";

    if (this.data.order.client) {
      const client: Company = this.data.order.client;
      clientName = client.name;
    }

    if (this.data.order.doctor) {
      const doctor: Individual = this.data.order.doctor;
      clientName = `${doctor.first_name} ${doctor.last_name}`;
    }

    if (this.data.order.patient) {
      const patient: Individual = this.data.order.patient;
      clientName = `${patient.first_name} ${patient.last_name}`;
    }



    this.printLabelsForm.patchValue({
      client: clientName,
      prescriber: this.data.order.prescriber ? this.data.order.prescriber.first_name  + " " + this.data.order.prescriber.last_name : ""
    })
  }

  async printSingle(orderItem: OrderItem): Promise<boolean> {

    const medicineDosagePackageType: MedicineDosagePackageType | null = orderItem.medicineDosagePackageType;

    if (!medicineDosagePackageType) {
      this._alertService.errorAlert("Invalid package type");
      return false;
    }

    const now: string = new Date().toISOString();

    const date = this._utilService.bestBeforeTime(now);

    const payload: PrintLabelPayload = {
      description: orderItem.medicineDosagePackageType.label_name,
      client: this.printLabelsForm.value.client,
      quantity: orderItem.quantity,
      medicine_number: orderItem.dosageType.medicine_number,
      dispensed_by: environment.printing.dispensed_by,
      contained_quantity: medicineDosagePackageType.contained_quantity,
      prescriber: this.printLabelsForm.value.prescriber,
      date: date,
      order_number: `x${orderItem.order_id}`
    }

    if (!this.largeSize) payload.size = "small";

    const success = await this._batchService
      .printLabel(payload);

    return success;
  }

  async print() {
    const orderItems: OrderItem[] = this.data.orderItems;

    let success: boolean = false;

    for (let i = this.currentItemNumber; i < orderItems.length; i++) {

      const success = await this.printSingle(orderItems[i]);

      if (!success) break;

      this.currentItemNumber += 1;

    }

    if (success) this._dialogRef.close(success);
    
  }



  async openSearchClientDialog() {

    this.selectedClient = await this._companyService
      .openCompanySearchDialog("Search for a client or company");

    if (!this.selectedClient) return;

    this
      .printLabelsForm
      .patchValue({
        client: this.selectedClient ? this.selectedClient.name : null
      });

  }

  deselectClient() {
    this.selectedClient = null;
    this
      .printLabelsForm
      .patchValue({
        client: null
      });
  }

  async openSearchPrescriberDialog() {
    this.selectedPrescriber = await this._companyService
      .openCompanySearchDialog("Search for a prescriber/company");

    if (!this.selectedPrescriber) return;
    this
      .printLabelsForm
      .patchValue({
        prescriber: this.selectedPrescriber ? this.selectedPrescriber.name : null
      });

  }

  deselectPrescriber() {
    this.selectedPrescriber = null;
    this
      .printLabelsForm
      .patchValue({
        prescriber: null
      });
  }
}
